import { LoginService, User, UserLoginDetail, UserService } from '@jooxter/api';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { UserQueryKeys } from './queryKeys';
import { UserStaleTimeEnum } from './staleTimes';
import { useCallback } from 'react';
import { AxiosError } from 'axios';

/**
 * This is a temporary hook for compatibility with the tablet application.
 * which cannot use the @jooxter/core package
 * This means the calls to users/me from within the @jooxter/floor-plan
 * will not trigger usual core logic such as logging out the user if the token is invalid.
 */
export const useFetchUser = (
  enabled = true
): {
  user: User | undefined;
  error?: AxiosError<User, null> | null;
  isLoading: boolean;
} => {
  const fetchUser = useCallback(() => {
    return UserService.getMe();
  }, []);

  const {
    data: user,
    error,
    isLoading,
  } = useQuery<User | undefined, AxiosError<User, null>>({
    queryKey: [UserQueryKeys.GetMe],
    queryFn: fetchUser,
    enabled,
    staleTime: 0,
    retry: 3,
  });

  return { user, error, isLoading };
};

export const useFetchLoginDetails = (
  email?: string
): { loginDetails?: UserLoginDetail | null; isPending: boolean; isLoading: boolean } => {
  const fetchLoginDetails = () => {
    if (email) {
      return LoginService.getUserLoginDetails(email);
    }

    return Promise.reject(null);
  };

  const {
    data: loginDetails,
    isPending,
    isLoading,
  } = useQuery({
    queryKey: [UserQueryKeys.GetUserLoginDetails, email, fetchLoginDetails],
    queryFn: () => fetchLoginDetails(),
    staleTime: UserStaleTimeEnum.GetUserLoginDetails,
    enabled: !!email,
    placeholderData: keepPreviousData,
  });

  return { loginDetails, isPending, isLoading };
};
